import { Matrix, Quaternion, Vector2, Vector3, Vector4 } from "@babylonjs/core/Maths/math.vector";
import { EPSILON, eq, geq, leq, PI_MUL2 } from "../../utils/math";
export var VECTOR2_X = new Vector2(1, 0);
export var VECTOR2_Y = new Vector2(0, 1);
export var VECTOR3_ZERO = new Vector3(0, 0, 0);
export var VECTOR3_MIN = new Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
export var VECTOR3_MAX = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
export var VECTOR3_X = new Vector3(1, 0, 0);
export var VECTOR3_Y = new Vector3(0, 1, 0);
export var VECTOR3_Z = new Vector3(0, 0, 1);
export var Orientation = {
    Collinear: 0,
    Clockwise: 1,
    CounterClockwise: 2
};
var Rectangle = /** @class */ (function () {
    function Rectangle(pP1, pP2) {
        this.min = new Vector2();
        this.max = new Vector2();
        this.set(pP1, pP2);
        if (pP1.x > pP2.x) {
            this.min.x = pP2.x;
            this.max.x = pP1.x;
        }
        if (pP1.y > pP2.y) {
            this.min.y = pP2.y;
            this.max.y = pP1.y;
        }
    }
    Rectangle.prototype.set = function (pP1, pP2) {
        this.min.copyFrom(pP1);
        this.max.copyFrom(pP2);
        if (pP1.x > pP2.x) {
            this.min.x = pP2.x;
            this.max.x = pP1.x;
        }
        if (pP1.y > pP2.y) {
            this.min.y = pP2.y;
            this.max.y = pP1.y;
        }
        return (this);
    };
    return Rectangle;
}());
export { Rectangle };
export function intersectRayPlane(pRay, pPlane) {
    var tIsecPoint = null;
    var tDot = Vector3.Dot(pRay.direction, pPlane.normal);
    if (tDot !== 0.0) {
        var t = -pPlane.signedDistanceTo(pRay.origin) / tDot;
        if (t >= 0.0) {
            var tDirS = gTmpVector3_1;
            pRay.direction.scaleToRef(t, tDirS);
            tIsecPoint = pRay.origin.add(tDirS);
        }
    }
    return (tIsecPoint);
}
export function intersectLineLine2(pS1, pE1, pS2, pE2) {
    var tIsecPoint = null;
    var tDx1 = pE1.x - pS1.x;
    var tDx2 = pE2.x - pS2.x;
    var tDx3 = pS1.x - pS2.x;
    var tDy1 = pE1.y - pS1.y;
    var tDy2 = pS1.y - pS2.y;
    var tDy3 = pE2.y - pS2.y;
    var tRatio = tDx1 * tDy3 - tDy1 * tDx2;
    if (!eq(tRatio, 0.0)) {
        tRatio = (tDy2 * tDx2 - tDx3 * tDy3) / tRatio;
        tIsecPoint = new Vector2(pS1.x + tRatio * tDx1, pS1.y + tRatio * tDy1);
    }
    return (tIsecPoint);
}
export function angleBetweenVectors2(pV1, pV2) {
    var tDot = Vector2.Dot(pV1, pV2);
    var tDotNorm = tDot / (pV1.length() * pV2.length());
    if (tDotNorm >= 1)
        return (0);
    if (tDotNorm <= -1)
        return (Math.PI);
    return (Math.acos(tDotNorm));
}
export function angleBetweenVectors3(pV1, pV2) {
    var tDot = Vector3.Dot(pV1, pV2);
    var tDotNorm = tDot / (pV1.length() * pV2.length());
    if (tDotNorm >= 1)
        return (0);
    if (tDotNorm <= -1)
        return (Math.PI);
    return (Math.acos(tDotNorm));
}
/**
 * Returns a signed 360° angle, range (-PI, PI).
 */
export function signedAngleBetweenVectors2(pV1, pV2) {
    var tDot = Vector2.Dot(pV1, pV2);
    var tDet = pV1.x * pV2.y - pV1.y * pV2.x;
    return (Math.atan2(tDet, tDot));
}
/**
 * Returns a signed 360° angle, range (-PI, PI).
 */
export function signedAngleBetweenVectors3(pV1, pV2, pPlaneNormal) {
    var tDot = Vector3.Dot(pV1, pV2);
    var tDet = Vector3.Dot(pPlaneNormal, Vector3.Cross(pV2, pV1));
    return (Math.atan2(tDet, tDot));
}
/**
 * Normalizes an angle to be in range (0, 2PI) or (-2PI, 2PI) if pSigned is true.
 */
export function normalizeAngle(pAngle, pSigned) {
    var tNewAngle = pAngle % PI_MUL2;
    if (!pSigned && tNewAngle < 0)
        tNewAngle = PI_MUL2 + tNewAngle;
    return (tNewAngle);
}
/**
 * Returns the shortest distance (signed) between 2 angles.
 */
export function angleDistance(pAngle1, pAngle2) {
    var tD = (pAngle1 - pAngle2) % PI_MUL2;
    if (tD > Math.PI)
        tD = PI_MUL2 - tD;
    else if (tD < -Math.PI)
        tD = -PI_MUL2 - tD;
    return (tD);
}
export function quaternionToAxisAngle(pQ) {
    //normalize pQ in order to avoid pQ.w values > 1 (and other issues)
    var tQ = pQ.clone().normalize();
    var tS = Math.sqrt(1.0 - tQ.w * tQ.w);
    if (tS < EPSILON)
        tS = 1.0;
    else
        tS = 1.0 / tS;
    var tAxis = new Vector3(tQ.x * tS, tQ.y * tS, tQ.z * tS);
    if (eq(tAxis.length(), 0.0))
        return (new Vector4(0, 0, 0, 0));
    else
        return (new Vector4(tAxis.x, tAxis.y, tAxis.z, Math.acos(tQ.w) * 2.0));
}
/**
 * Returns a rotation for a target coordinate system.
 * The 3 given axes need to be normalized and orthogonal to each other.
 */
export function quaternionFromAxes(pX, pY, pZ) {
    var tQuaternion = new Quaternion();
    quaternionFromAxesToRef(pX, pY, pZ, tQuaternion);
    return (tQuaternion);
}
/**
 * Returns a rotation for a target coordinate system.
 * The 3 given axes need to be normalized and orthogonal to each other.
 */
export function quaternionFromAxesToRef(pX, pY, pZ, pResult) {
    Matrix.FromXYZAxesToRef(pX, pY, pZ, gTmpMatrix);
    Quaternion.FromRotationMatrixToRef(gTmpMatrix, pResult);
}
export function rotateVector2(pV, pAngle) {
    var tV = Vector2.Zero();
    rotateVector2ToRef(pV, pAngle, tV);
    return (tV);
}
export function rotateVector2ToRef(pV, pAngle, pResult) {
    var tSin = Math.sin(pAngle);
    var tCos = Math.cos(pAngle);
    pResult.x = (pV.x * tCos) - (pV.y * tSin);
    pResult.y = (pV.y * tCos) + (pV.x * tSin);
}
export function rotateVector3(pV, pRot) {
    var tV = Vector3.Zero();
    rotateVector3ToRef(pV, pRot, tV);
    return (tV);
}
export function rotateVector3ToRef(pV, pRot, pResult) {
    //pResult = pRot * pV * conjugate(pRot)
    /*let tQuat = pRot.multiply(new Quaternion(pV.x, pV.y, pV.z, 0));
    tQuat.multiplyInPlace(pRot.conjugate());

    pResult.x = tQuat.x;
    pResult.y = tQuat.y;
    pResult.z = tQuat.z;*/
    //faster variant found here: http://blog.molecular-matters.com/2013/05/24/a-faster-quaternion-vector-multiplication/
    //t = 2 * cross(pRotation.xyz, pVector)
    //pResult = pVector + pRotation.w * t * cross(pRotation.xyz, t)
    var tx = 2 * (pRot.y * pV.z - pRot.z * pV.y);
    var ty = 2 * (pRot.z * pV.x - pRot.x * pV.z);
    var tz = 2 * (pRot.x * pV.y - pRot.y * pV.x);
    pResult.x = pV.x + pRot.w * tx + (pRot.y * tz - pRot.z * ty);
    pResult.y = pV.y + pRot.w * ty + (pRot.z * tx - pRot.x * tz);
    pResult.z = pV.z + pRot.w * tz + (pRot.x * ty - pRot.y * tx);
}
/**
 * Returns a vector which is perpendicular to pV.
 */
export function perpendicularVector2(pV) {
    return (new Vector2(pV.y, -pV.x));
}
/**
 * Returns a "random" vector which is perpendicular to pV.
 */
export function perpendicularVector3(pV) {
    var tVPer = Vector3.Cross(pV, VECTOR3_X);
    if (tVPer.lengthSquared() < EPSILON)
        tVPer = Vector3.Cross(pV, VECTOR3_Y);
    return (tVPer.normalize());
}
export function projectPointOnPlane(pP, pPlane) {
    var tDistance = pPlane.signedDistanceTo(pP);
    var tDir = gTmpVector3_1;
    pPlane.normal.scaleToRef(-tDistance, tDir);
    return (pP.add(tDir));
}
export function projectPointOnLine2(pP, pLS, pLE, pAsSegment) {
    if (pAsSegment === void 0) { pAsSegment = false; }
    var tV = Vector2.Zero();
    projectPointOnLine2ToRef(pP, pLS, pLE, pAsSegment, tV);
    return (tV);
}
export function projectPointOnLine2ToRef(pP, pLS, pLE, pAsSegment, pResult) {
    var tV = gTmpVector2_1;
    var tW = gTmpVector2_2;
    pLE.subtractToRef(pLS, tV);
    pP.subtractToRef(pLS, tW);
    var tC1 = tV.x * tW.x + tV.y * tW.y;
    if (pAsSegment && (tC1 < 0 || eq(tC1, 0.0))) {
        pResult.copyFrom(pLS);
        return;
    }
    var tC2 = tV.x * tV.x + tV.y * tV.y;
    if (pAsSegment && (tC2 < tC1 || eq(tC2, tC1))) {
        pResult.copyFrom(pLE);
        return;
    }
    if (tC2 === 0.0) {
        pResult.copyFrom(pLE);
        return;
    }
    var tRatio = tC1 / tC2;
    pResult.x = pLS.x + tRatio * tV.x;
    pResult.y = pLS.y + tRatio * tV.y;
}
export function projectPointOnLine3(pP, pLS, pLE, pAsSegment) {
    if (pAsSegment === void 0) { pAsSegment = false; }
    var tV = Vector3.Zero();
    projectPointOnLine3ToRef(pP, pLS, pLE, pAsSegment, tV);
    return (tV);
}
export function projectPointOnLine3ToRef(pP, pLS, pLE, pAsSegment, pResult) {
    var tV = gTmpVector3_1;
    var tW = gTmpVector3_2;
    pLE.subtractToRef(pLS, tV);
    pP.subtractToRef(pLS, tW);
    var tC1 = tV.x * tW.x + tV.y * tW.y + tV.z * tW.z;
    if (pAsSegment && (tC1 < 0 || eq(tC1, 0.0))) {
        pResult.copyFrom(pLS);
        return;
    }
    var tC2 = tV.x * tV.x + tV.y * tV.y + tV.z * tV.z;
    if (pAsSegment && (tC2 < tC1 || eq(tC2, tC1))) {
        pResult.copyFrom(pLE);
        return;
    }
    if (tC2 === 0.0) {
        pResult.copyFrom(pLE);
        return;
    }
    var tRatio = tC1 / tC2;
    pResult.x = pLS.x + tRatio * tV.x;
    pResult.y = pLS.y + tRatio * tV.y;
    pResult.z = pLS.z + tRatio * tV.z;
}
export function orientationToLine2(pP, pLS, pLE) {
    var tO = (pLE.y - pLS.y) * (pP.x - pLE.x) - (pLE.x - pLS.x) * (pP.y - pLE.y);
    if (eq(tO, 0))
        return (Orientation.Collinear);
    else
        return (tO > 0 ? Orientation.CounterClockwise : Orientation.Clockwise);
}
export function triangleNormal(pP1, pP2, pP3) {
    var tV = Vector3.Zero();
    triangleNormalToRef(pP1, pP2, pP3, tV);
    return (tV);
}
export function triangleNormalToRef(pP1, pP2, pP3, pResult) {
    pP2.subtractToRef(pP1, gTmpVector3_1); //edge 1
    pP3.subtractToRef(pP1, gTmpVector3_2); //edge 2
    Vector3.CrossToRef(gTmpVector3_1, gTmpVector3_2, pResult);
    pResult.normalize();
}
/**
 * Returns true if the point is lying inside the frustum.
 */
export function isPointInFrustum(pP, pFrustum) {
    for (var i = 0; i < 6; ++i) {
        if (pFrustum[i].dotCoordinate(pP) < 0)
            return (false);
    }
    return (true);
}
/**
 * This functions performs only a fast check.
 * Returns false if the triangle is definitely outside of the frustum. If true is returned the
 * triangle MAY be inside of the frustum.
 */
export function isTriangleInFrustum(pP1, pP2, pP3, pFrustum) {
    for (var i = 0; i < 6; ++i) {
        var tInCount = 3;
        if (pFrustum[i].dotCoordinate(pP1) < 0) {
            --tInCount;
            if (pFrustum[i].dotCoordinate(pP2) < 0) {
                --tInCount;
                if (pFrustum[i].dotCoordinate(pP3) < 0) {
                    --tInCount;
                }
            }
        }
        if (tInCount === 0)
            return (false);
    }
    return (true);
}
export function isPointInSegmentBounds2(pP, pSS, pSE) {
    return (geq(pP.x, Math.min(pSS.x, pSE.x)) && leq(pP.x, Math.max(pSS.x, pSE.x)) &&
        geq(pP.y, Math.min(pSS.y, pSE.y)) && leq(pP.y, Math.max(pSS.y, pSE.y)));
}
export function isPointOnSegment2(pP, pSS, pSE) {
    return (isPointInSegmentBounds2(pP, pSS, pSE) && isCollinear2(pSS, pSE, pP));
}
export function isPointInTriangle2(pPoint, pP1, pP2, pP3) {
    //based on: https://stackoverflow.com/questions/2049582/how-to-determine-if-a-point-is-in-a-2d-triangle
    var s = pP1.y * pP3.x - pP1.x * pP3.y + (pP3.y - pP1.y) * pPoint.x + (pP1.x - pP3.x) * pPoint.y;
    var t = pP1.x * pP2.y - pP1.y * pP2.x + (pP1.y - pP2.y) * pPoint.x + (pP2.x - pP1.x) * pPoint.y;
    if ((s < 0) != (t < 0))
        return (false);
    var a = -pP2.y * pP3.x + pP1.y * (pP3.x - pP2.x) + pP1.x * (pP2.y - pP3.y) + pP2.x * pP3.y;
    return (a < 0 ? (s <= 0 && s + t >= a) : (s >= 0 && s + t <= a));
}
/**
 * Returns true if point 1, 2 and 3 are collinear.
 */
export function isCollinear2(pP1, pP2, pP3) {
    return (eq((pP2.x - pP1.x) * (pP3.y - pP1.y), (pP3.x - pP1.x) * (pP2.y - pP1.y)));
}
export function isMirrorScaling3(pScale) {
    return ((pScale.x * pScale.y * pScale.z) < 0.0);
}
/**
 * Returns true if line 1 is parallel to line 2.
 */
export function isParallel2(pS1, pE1, pS2, pE2) {
    return (eq((pS1.y - pE1.y) * (pS2.x - pE2.x), (pS2.y - pE2.y) * (pS1.x - pE1.x)));
}
export function segmentsIntersecting2(pS1, pE1, pS2, pE2) {
    //based on: http://www.geeksforgeeks.org/check-if-two-given-line-segments-intersect/
    var tO1 = orientationToLine2(pS2, pS1, pE1);
    var tO2 = orientationToLine2(pE2, pS1, pE1);
    var tO3 = orientationToLine2(pS1, pS2, pE2);
    var tO4 = orientationToLine2(pE1, pS2, pE2);
    if (tO1 !== tO2 && tO3 !== tO4)
        return (true);
    if (tO1 === Orientation.Collinear && isPointInSegmentBounds2(pS2, pS1, pE1))
        return (true);
    if (tO2 === Orientation.Collinear && isPointInSegmentBounds2(pE2, pS1, pE1))
        return (true);
    if (tO3 === Orientation.Collinear && isPointInSegmentBounds2(pS1, pS2, pE2))
        return (true);
    if (tO4 === Orientation.Collinear && isPointInSegmentBounds2(pE1, pS2, pE2))
        return (true);
    return (false);
}
export function rectanglesIntersecting(pR1, pR2) {
    return ((pR1.min.x <= pR2.max.x) && (pR1.max.x >= pR2.min.x) &&
        (pR1.min.y <= pR2.max.y) && (pR1.max.y >= pR2.min.y));
}
export function segmentRectangleIntersecting(pP1, pP2, pRect) {
    if (rectanglesIntersecting(pRect, gTmpRectangle.set(pP1, pP2))) {
        var tO = orientationToLine2(pRect.min, pP1, pP2);
        if (orientationToLine2(pRect.max, pP1, pP2) === tO &&
            orientationToLine2(gTmpVector2_1.set(pRect.min.x, pRect.max.y), pP1, pP2) === tO &&
            orientationToLine2(gTmpVector2_2.set(pRect.max.x, pRect.min.y), pP1, pP2) === tO) {
            return (false);
        }
        else {
            return (true);
        }
    }
    else {
        return (false);
    }
}
export function triangleRectangleIntersecting(pP1, pP2, pP3, pRect) {
    return (segmentRectangleIntersecting(pP1, pP2, pRect) ||
        segmentRectangleIntersecting(pP2, pP3, pRect) ||
        segmentRectangleIntersecting(pP3, pP1, pRect) ||
        isPointInTriangle2(pRect.min, pP1, pP2, pP3)); //to check if rectangle is completely inside the triangle
}
//// temp variables ////////////////////////////////////////////////////////////////////////////
var gTmpVector2_1 = Vector2.Zero();
var gTmpVector2_2 = Vector2.Zero();
var gTmpVector3_1 = Vector3.Zero();
var gTmpVector3_2 = Vector3.Zero();
var gTmpMatrix = Matrix.Identity();
var gTmpRectangle = new Rectangle(Vector2.Zero(), Vector2.Zero());
